import React, { forwardRef, useContext } from 'react';
import { useMergeRefs, FloatingPortal } from '@floating-ui/react';

import { AppContainerContext, Comp, getContainerStylingTransientProps, useTheme } from '@redsift/design-system';
import { ToggletipContentProps } from './types';
import { ToggletipPlacement, useToggletipContext } from '../toggletip';
import { StyledToggletipContent } from './styles';
import classNames from 'classnames';

const COMPONENT_NAME = 'ToggletipContent';
const CLASSNAME = 'redsift-toggletip-content';

/**
 * The ToggletipContent component.
 */
export const ToggletipContent: Comp<ToggletipContentProps, HTMLDivElement> = forwardRef((props, ref) => {
  const { transientProps, otherProps } = getContainerStylingTransientProps(props);

  const { children, className, style, theme: propsTheme } = otherProps;
  const appContainerState = useContext(AppContainerContext);
  const {
    getFloatingProps,
    isOpen,
    placement,
    refs,
    strategy,
    x,
    y,
    middlewareData: { arrow: { x: arrowX, y: arrowY } = {} },
  } = useToggletipContext();
  const theme = useTheme ? useTheme(propsTheme) : undefined;
  const popoverRef = useMergeRefs([refs.setFloating, ref]);

  const { arrowRef } = useToggletipContext();

  const staticSide = {
    top: 'bottom',
    right: 'left',
    bottom: 'top',
    left: 'right',
  }[placement.split('-')[0]];

  return (
    <FloatingPortal root={appContainerState?.appContainerRef.current}>
      {isOpen && (
        <StyledToggletipContent
          className={classNames(ToggletipContent.className, className)}
          ref={popoverRef}
          $theme={theme!}
          {...getFloatingProps(otherProps)}
          {...transientProps}
          style={{
            position: strategy,
            top: y ?? 0,
            left: x ?? 0,
            visibility: x == null ? 'hidden' : 'visible',
            ...style,
          }}
          $placement={placement as ToggletipPlacement}
        >
          <div
            ref={arrowRef}
            className={`${ToggletipContent.className}__arrow`}
            style={{
              left: arrowX != null ? `${arrowX}px` : '',
              top: arrowY != null ? `${arrowY}px` : '',
              [staticSide!]: '-6px',
            }}
          />
          <div className={`${ToggletipContent.className}__inner`}>{children}</div>
        </StyledToggletipContent>
      )}
    </FloatingPortal>
  );
});
ToggletipContent.className = CLASSNAME;
ToggletipContent.displayName = COMPONENT_NAME;
